import dayjs from "dayjs";
import getConfig from "next/config";

import api from "@api";
import { i18n } from "@lib/i18n";

import { TEST_DRIVE_TYPE_ID, IS_SERVER } from "@shared/constants";
import { fetchParkopolyAvailableSlots } from "@lib/test-drive/fetchParkopolyAvailableSlots";
import RoutingUtils from "@shared/routing/utils";

const { publicRuntimeConfig } = getConfig();
const { ROOT_URL_PREFIX } = publicRuntimeConfig;

const fetchClosestAvailableSlots = async (body, params) => {
  const parkopolyAvailableSlots = await fetchParkopolyAvailableSlots(
    body,
    params
  );

  if (parkopolyAvailableSlots.length > 0) {
    return parkopolyAvailableSlots;
  }

  const nextStartDate = dayjs(params.startDate)
    .add(90, "days")
    .format("YYYY-MM-DD");

  return fetchParkopolyAvailableSlots(body, {
    ...params,
    startDate: nextStartDate,
  });
};

const getTestDriveExclusionDates = async (testDriveType, language) => {
  const { data } = await api.getTestDriveExclusionDates(
    testDriveType,
    language
  );

  return data;
};

const serverGetInitialProps = async ctx => {
  const { req, store } = ctx;
  const { appConfig } = store.getState();

  const routingUtils = new RoutingUtils({
    routes: appConfig.routes,
  });

  const { session } = req;

  const isParkopolyEnabled =
    appConfig.configuration.app.discoveryJourney.parkopoly.enabled;

  const { testDrivePointSlug } = routingUtils.getParamsFromPathname(
    ctx.asPath.replace(ROOT_URL_PREFIX, "").replace(/\?.+$/, "")
  );

  const targetSlug = decodeURIComponent(testDrivePointSlug).replace(
    /['"’]/g,
    ""
  );

  try {
    const testDriveLocation =
      session.testDrive.point?.type === TEST_DRIVE_TYPE_ID.HOME
        ? session.testDrive.point
        : await api
            .getTestDriveLocationBySlug(targetSlug, req.i18n.language)
            .then(response => response.data);

    if (!testDriveLocation) {
      return { errorStatusCode: 404, namespacesRequired: ["common"] };
    }

    const startDate = session.testDrive.selectedSlot
      ? dayjs(session.testDrive.selectedSlot.startDateTime).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    let parkopolyAvailableSlots = null;

    if (
      isParkopolyEnabled &&
      [
        TEST_DRIVE_TYPE_ID.STORE,
        TEST_DRIVE_TYPE_ID.LAMAISON,
        TEST_DRIVE_TYPE_ID.HOME,
      ].includes(testDriveLocation.type)
    ) {
      parkopolyAvailableSlots = await fetchClosestAvailableSlots(
        { location: testDriveLocation },
        { startDate }
      );
    }

    const testDriveExclusionDates = await getTestDriveExclusionDates(
      testDriveLocation.type,
      req.i18n.language
    );

    return {
      namespacesRequired: ["common"],
      testDriveSessionData: session.testDrive,
      testDriveTypes: appConfig.testDriveTypes,
      testDriveLocation,
      testDriveExclusionDates,
      parkopolyAvailableSlots,
    };
  } catch (error) {
    const debug = require("debug")("app:test-drive");

    debug(`❗️ Couldn't get initial props \n\n`, error);

    return {
      errorStatusCode: error?.response?.status ?? 0,
      namespacesRequired: ["common"],
    };
  }
};

const clientGetInitialProps = async ctx => {
  const { store } = ctx;
  const state = store.getState();

  const routingUtils = new RoutingUtils({
    routes: state.appConfig.routes,
  });

  const { testDrivePointSlug } = routingUtils.getParamsFromPathname(
    ctx.asPath.replace(ROOT_URL_PREFIX, "").replace(/\?.+$/, "")
  );

  const isParkopolyEnabled =
    state.appConfig.configuration.app.discoveryJourney.parkopoly.enabled;

  try {
    const testDriveLocation =
      state.testDrive.point.type === TEST_DRIVE_TYPE_ID.HOME
        ? state.testDrive.point
        : await api
            .getTestDriveLocationBySlug(decodeURIComponent(testDrivePointSlug))
            .then(response => response.data);

    if (!testDriveLocation) {
      return { errorStatusCode: 404, namespacesRequired: ["common"] };
    }

    let parkopolyAvailableSlots = null;

    if (
      isParkopolyEnabled &&
      [
        TEST_DRIVE_TYPE_ID.STORE,
        TEST_DRIVE_TYPE_ID.LAMAISON,
        TEST_DRIVE_TYPE_ID.HOME,
      ].includes(testDriveLocation.type)
    ) {
      parkopolyAvailableSlots = await fetchClosestAvailableSlots(
        { location: testDriveLocation },
        { startDate: dayjs().format("YYYY-MM-DD") }
      );
    }

    const testDriveExclusionDates = await getTestDriveExclusionDates(
      testDriveLocation.type,
      i18n.language
    );

    return {
      namespacesRequired: ["common"],
      testDriveTypes: state.appConfig.testDriveTypes,
      testDriveLocation,
      testDriveExclusionDates,
      parkopolyAvailableSlots,
    };
  } catch (exception) {
    return { errorStatusCode: 0, namespacesRequired: ["common"] };
  }
};

export const getInitialProps = async ctx => {
  return IS_SERVER ? serverGetInitialProps(ctx) : clientGetInitialProps(ctx);
};
